import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TradingAccountRestrictions, isRestricted } from '@utils/trading-account.utils'

import { LeverageTypeDto } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'

export const useLeverageOptions = (
  platformTypeId?: number,
  currentLeverageId?: string | number,
  restrictions: NameDto[] = []
) => {
  const apiClient = useApiClient(ClientApiClient)

  const { data: leverages = [], isLoading } = useQuery({
    queryKey: queryKeys.tradingAccount.leverageTypes(platformTypeId),
    queryFn: () => apiClient.getLeverageTypes(platformTypeId!),
    enabled: platformTypeId !== undefined,
  })

  const isLeverageDisabled = useCallback(
    (leverage: LeverageTypeDto) => {
      const leverageUpRestriction = isRestricted(
        TradingAccountRestrictions.LEVERAGE_UP,
        restrictions
      )
      const leverageDownRestriction = isRestricted(
        TradingAccountRestrictions.LEVERAGE_DOWN,
        restrictions
      )
      const currentLeverage = leverages.find(
        (l) => l.id.toString() === currentLeverageId?.toString()
      )

      if (!currentLeverage) return false

      return (
        (leverageUpRestriction && currentLeverage.amount < leverage.amount) ||
        (leverageDownRestriction && currentLeverage.amount > leverage.amount)
      )
    },
    [currentLeverageId, leverages, restrictions]
  )

  const leverageOptions = useMemo(() => {
    return leverages.map((lev) => ({
      label: lev.name,
      value: lev.id,
      disabled: isLeverageDisabled(lev),
    }))
  }, [leverages, isLeverageDisabled])

  return { leverages, leverageOptions, isLoading }
}
